//HistoryRecord.tsx
import React, { useEffect, useState, useMemo } from "react";
import { getProgressInquiryPageData } from "../../../../service/ProgressInquiryPageService";
import { ProgressInquiryPageType } from "../../../../pages/ProgressInquiry/data/ProgressInquiryPageType";
import {
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from "@mui/material";
import theme from "../../../../style/theme";
import InquiryActivityStepper from "./InquiryActivityStepper";
import { useNavigate } from "react-router-dom";
import NotificationImportantRoundedIcon from "@mui/icons-material/NotificationImportantRounded";
import { useUser } from "../../../../context/UserContext";
import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import dayjs from "dayjs";

/**
 * 進捗照会履歴一覧コンポーネント
 * - 申請の進捗状況を一覧表示し、検索・ソート・無限スクロールを提供する
 * - ログインユーザーの申請のみ表示する
 */
const HistoryRecords: React.FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const navigate = useNavigate();
  const [allRecords, setAllRecords] = useState<ProgressInquiryPageType[]>([]);
  const [visibleRecords, setVisibleRecords] = React.useState<
    ProgressInquiryPageType[]
  >([]); // 表示する記録
  const [loading, setLoading] = React.useState<boolean>(false); // ローディング中か否か
  const [allLoaded, setAllLoaded] = React.useState(false); // すべての記録をローディング完了か否か
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">(() => {
    // 順序並び方(ボタンを1回でも押している場合は、sessionStorageから復元)
    const saved = sessionStorage.getItem("sortOrder_Progress");
    return saved === "asc" || saved === "desc" ? saved : "desc";
  });
  const [filterText, setFilterText] = React.useState<string>(() => {
    // テキストフィルター(テキストが入っている場合は、sessionStorageから復元)
    return sessionStorage.getItem("filterText_Progress") || "";
  });
  const [loadedCount, setLoadedCount] = React.useState<number>(0); // ローディングされた記録数
  const { user } = useUser(); // useUser フックを使ってユーザー情報を取得
  const statusMapping: { [key: string]: string } = {
    //申請ステータスの日本語マーピング
    NotStarted: "申請前",
    InProgress: "申請中",
    Completed: "完了",
    Canceled: "取り下げ",
    Failed: "却下",
    Error: "エラー終了",
    SendBack: "差し戻し",
    Draft: "一時保存"
  };

  //フィルター選択肢
  const [filterOptions, setFilterOptions] = React.useState<{
    [key: string]: boolean;
  }>(() => {
    // sessionStorageにフィルター選択肢の値が残っている場合は、sessionStorage から復元。それ以外は初期値。
    const saved = sessionStorage.getItem("filterOptions_Progress");
    return saved
      ? JSON.parse(saved)
      : {
        myApplication: true, // デフォルト的に「私の申請」を選ぶ
        all: false, // デフォルト的に「すべて」を選ばない

        completed: false, // デフォルト的に「完了」を選ばない
        pendingApproval: false, // デフォルト的に「処理中」を選ばない
        canceled: false, // デフォルト的に「取り下げ」を選ばない
        sendBack: false, // デフォルト的に「差し戻し」を選ばない
        draft: false, // デフォルト的に「一時保存」を選ばない
        allState: true, // デフォルト的に「すべて」(状態)を選ぶ

        urgentRequest: false, // デフォルト的に「急ぎ申請」を選ばない
        allUrgency: false, //デフォルト的に「すべて」(緊急度)を選ばない
      };
  });

  // フィルターラベルを定義
  const filterLabels = [
    { category: "範囲:", key: "myApplication", label: "私の申請" },
    { category: "範囲:", key: "all", label: "すべて" },

    { category: "ステータス:", key: "completed", label: "完了" },
    { category: "ステータス:", key: "pendingApproval", label: "申請中" },
    { category: "ステータス:", key: "sendBack", label: "差し戻し" },
    { category: "ステータス:", key: "canceled", label: "取り下げ" },
    { category: "ステータス:", key: "draft", label: "一時保存" },
    { category: "ステータス:", key: "allState", label: "すべて" },

    // { category: '緊急度:',key: 'urgentRequest', label: '急ぎ申請' },
    // { category: '緊急度:',key: 'allUrgency', label: 'すべて' },
  ];

  const handleCardClick = (record: ProgressInquiryPageType) => {
    // 遷移後画面
    navigate(`/detail/${record.id}`, {
      state: { ...record, from: "HistoryRecord" },
    });
  };

  // **1. filterOptions(フィルターラベル)の変化を監視してデータを再取得**
  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const data = await getProgressInquiryPageData();
        setAllRecords(data);
      } catch (error) {
        console.error("Error fetching records:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [filterOptions]);

  // **2.フィルター条件設定：範囲・申請ステータス・緊急度によって分類する*
  const filteredRecords = React.useMemo(() => {
    return allRecords.filter((record) => {
      let matches = true;
      if (filterOptions.all) {
        matches = matches && true;
      } else {
        if (filterOptions.myApplication) {
          matches = matches && record.applicantId === user?.id; // 「私の申請」：ログインしている者の記録だけを表示
        }
      }
      //申請状態によって分類する
      if (!filterOptions.allState) {
        if (filterOptions.pendingApproval) {
          matches = matches && record.applicationStatus === "InProgress"; //「申請中」：申請状態は'申請中'の場合
        }
        if (filterOptions.completed) {
          matches = matches && record.applicationStatus === "Completed"; //「完了」：申請状態は'完了'の場合
        }
        if (filterOptions.canceled) {
          matches = matches && record.applicationStatus === "Canceled"; //「取り下げ」：申請状態は'取り下げ'の場合
        }
        if (filterOptions.sendBack) {
          matches = matches && record.applicationStatus === "SendBack"; //「差し戻し」：申請状態は'差し戻し'の場合
        }
        if (filterOptions.draft) {
          matches = matches && record.applicationStatus === "Draft"; //「一時保存」：申請状態は'一時保存'の場合
        }
      }

      // 緊急度によって分類する
      // if (!filterOptions.allUrgency) {
      //   if (filterOptions.urgentRequest) {
      //     matches =matches &&record.isUrgent === true;//「急ぎ申請」：緊急度は'急ぎ申請'
      //   }
      // }

      //テキストで記録をフィルタリングする
      if (filterText.trim() !== "") {
        const recordText =
          `${record.applicationNumber} ${record.applicationType} ${record.applicationFormat} ${dayjs(record.submittedAt).isValid() ? dayjs(record.submittedAt).format("YYYY/MM/DD") : "未提出"} ${record.applicant} ${record.userGroup} ${record.userProfiles} ${record.applicationStatus}`.toLowerCase();
        matches = matches && recordText.includes(filterText.toLowerCase());
      }

      return matches;
    });
  }, [
    allRecords,
    filterOptions.all,
    filterOptions.allState,
    filterOptions.canceled,
    filterOptions.completed,
    filterOptions.myApplication,
    filterOptions.pendingApproval,
    filterOptions.sendBack,
    filterOptions.draft,
    filterText,
    user?.groups,
    user?.id,
    user?.profiles,
  ]);

  // **2. フィルタリングした記録の並び順**
  const sortedFilteredRecords = React.useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      // submittedAtが空または"未提出"の場合を判定
      const isAEmpty = !dayjs(a.submittedAt).isValid();
      const isBEmpty = !dayjs(b.submittedAt).isValid();

      // 両方とも空の場合は順序を維持
      if (isAEmpty && isBEmpty) return 0;

      // aのみ空の場合は、aを後ろに配置
      if (isAEmpty) return 1;

      // bのみ空の場合は、bを後ろに配置
      if (isBEmpty) return -1;

      // 両方とも空でない場合は、日付で並び替え
      return sortOrder === "desc"
        ? new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
        : new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
    });
    // .sort((a, b) => {
    //   // 急ぎ申請は上に表示
    //   if (a.isUrgent === true && b.isUrgent === false) return -1;
    //   if (b.isUrgent === true && a.isUrgent === false) return 1;
    //   return 0;
    // });
  }, [filteredRecords, sortOrder]);

  // **3. sortedFilteredRecords(labelでフィリングしたfilteredRecordsと並び順sortOrderで決める)の変化を監視して表示する記録を更新**
  React.useEffect(() => {
    const initialRecords = sortedFilteredRecords.slice(0, 3);
    setVisibleRecords(initialRecords);
    setLoadedCount(initialRecords.length);
    setAllLoaded(initialRecords.length >= sortedFilteredRecords.length);
  }, [sortedFilteredRecords]);

  // **4. labelをクリックする**
  const handleTagClick = (optionKey: string) => {
    // 対象カテゴリを取得
    const targetCategory = filterLabels.find((label) => label.key === optionKey)?.category;
    if (!targetCategory) return;

    setFilterOptions((prevOptions) => {
      // 同カテゴリに属するキー一覧
      const categoryKeys = filterLabels
        .filter((label) => label.category === targetCategory)
        .map((label) => label.key);

      const isCurrentlyOn = !!prevOptions[optionKey];
      const onCountInCategory = categoryKeys.reduce(
        (acc, key) => acc + (prevOptions[key] ? 1 : 0),
        0
      );

      // すでにカテゴリで唯一ONのラベルを再クリックした場合はOFFにしない（維持）
      if (isCurrentlyOn && onCountInCategory === 1) {
        return prevOptions;
      }

      // クリックされたキーをON、同カテゴリの他キーをOFFにする（常に1つ選択）
      const updatedOptions: typeof prevOptions = { ...prevOptions };
      updatedOptions[optionKey] = true;
      categoryKeys
        .filter((key) => key !== optionKey)
        .forEach((key) => {
          updatedOptions[key] = false;
        });

      // sessionStorageへ保存（HistoryRecord.tsxでは Progress を使用）
      sessionStorage.setItem("filterOptions_Progress", JSON.stringify(updatedOptions));
      return updatedOptions;
    });
  };

  // **5.もっと多くの記録をローディング**
  const loadMoreRecords = () => {
    if (loading || allLoaded) return;
    setLoading(true);
    setTimeout(() => {
      const nextRecords = sortedFilteredRecords.slice(0, loadedCount + 3);
      setVisibleRecords(nextRecords);
      setLoadedCount(nextRecords.length);
      setLoading(false);
      setAllLoaded(nextRecords.length >= sortedFilteredRecords.length);
    }, 500);
  };

  // **6.  スクロールイベントを監視する**
  React.useEffect(() => {
    const handleScroll = () => {
      // 現在のスクロール位置とページの高さを取得する
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.body.offsetHeight;

      if (scrollPosition >= documentHeight - 100) {
        // ページの底から100pxの位置で、さらに読み込む処理を実行する
        if (!loading && visibleRecords.length < sortedFilteredRecords.length) {
          console.log("Start loading more records...");
          loadMoreRecords();
        }
      }
    };

    // スクロールイベントを監視する
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loading, visibleRecords, sortedFilteredRecords.length, loadMoreRecords]);

  // **7. (日付による)並び順を変更**
  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => {
      const newOrder = prevOrder === "desc" ? "asc" : "desc";
      sessionStorage.setItem("sortOrder_Progress", newOrder); // 並び順をsessionStorageに保存
      return newOrder;
    });
  };

  // フィルターの条件を初期化する
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem("filterOptions_Progress");
    sessionStorage.removeItem("sortOrder_Progress");
    sessionStorage.removeItem("filterText_Progress");

    // フィルター条件の初期化
    setFilterText("");
    setSortOrder("desc");
    setFilterOptions({
      myApplication: true, // デフォルト的に「私の申請」を選ぶ
      all: false, // デフォルト的に「すべて」を選ばない

      completed: false, // デフォルト的に「完了」を選ばない
      pendingApproval: false, // デフォルト的に「処理中」を選ばない
      canceled: false, // デフォルト的に「取り下げ」を選ばない
      sendBack: false, // デフォルト的に「差し戻し」を選ばない
      draft: false, // デフォルト的に「一時保存」を選ばない
      allState: true, // デフォルト的に「すべて」(状態)を選ぶ

      urgentRequest: false, // デフォルト的に「急ぎ申請」を選ばない
      allUrgency: false, //デフォルト的に「すべて」(緊急度)を選ばない
    });
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    // フィルター検索のテキストを初期化
    setFilterText("");
    // sessionStorageの値をクリアする
    sessionStorage.removeItem("filterText_Progress");
  };

  //Loading表示
  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  };

  return (
    <Box>
      {/* フィルタ入力 */}
      <Box display="flex" alignItems="center" sx={{ m: 2 }}>
        <TextField
          label="フィルタを入力"
          variant="outlined"
          size="small"
          value={filterText}
          onChange={(e) => {
            const newValue = e.target.value;
            setFilterText(newValue);
            sessionStorage.setItem("filterText_Progress", newValue); // 入力したテキストをsessionStorageに格納する。
          }}
          sx={{ ml: 1, width: 220 }}
          slotProps={{
            input: {
              endAdornment: filterText && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleFilterTextClear}
                    edge="end"
                    sx={{
                      backgroundColor: "transparent", // 背景透明
                      "&:hover": {
                        backgroundColor: "transparent", // ホバー時も透明
                      },
                    }}
                  >
                    <ClearSharpIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        {/* 順序並びボタン */}
        <Button onClick={toggleSortOrder}>
          {sortOrder === "desc" ? "申請日 昇順" : "申請日 降順"}
        </Button>
      </Box>

      {/* フィルター機能 */}
      <Box
        sx={{
          display: "flex",
          ml: 1,
          gap: 2,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* {['範囲:', 'ステータス:',"緊急度:"].map((category) => ( */}
        {["範囲:", "ステータス:"].map((category) => (
          <Box
            key={category}
            sx={{
              mb: isMobile ? 0 : 2,
              display: "flex",
              ml: 2,
              alignItems: "center",
            }}
          >
            <Box display="flex" sx={{ gap: 1, flexWrap: isMobile ? 'wrap' : 'nowrap', maxWidth: isMobile ? '327px' : 'none' }}>
              {/* フィルターラベル */}
              <Typography noWrap={isMobile} variant="subtitle1" sx={{ mr: 1 }}>
                {category}
              </Typography>
              {/* <Box display="flex" sx={{ gap: 1 }}> */}
              {filterLabels
                .filter((label) => label.category === category)
                .map(({ key, label }) => (
                  <Chip
                    key={key}
                    label={label}
                    clickable
                    onClick={() => handleTagClick(key)}
                    sx={{
                      backgroundColor: filterOptions[key]
                        ? theme.palette.custom.blue
                        : "grey.200",
                      color: "text.primary",
                      "&:hover": {
                        backgroundColor: filterOptions[key]
                          ? "primary.dark"
                          : "grey.400",
                      },
                    }}
                  />
                ))}
            </Box>
          </Box>
        ))}
        {/* フィルターのリセット機能(初期化して、sessionStorageをclearにする) */}
        <Box
          sx={{
            mb: isMobile ? 0 : 2,
            display: "flex",
            ml: 2,
            alignItems: "center",
          }}
        >
          <Chip
            label="リセット"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
            onClick={filterReset}
          />
        </Box>
      </Box>
      {/* 申請記録カード */}
      {visibleRecords.map((record) => (
        <Card
          key={record.id}
          sx={{
            margin: 3,
            cursor: "pointer",
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": {
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#f9f9f9",
            },
          }}
          onClick={(event) => {
            event.stopPropagation(); // クリックエリアでイベントバブリング停止
            handleCardClick(record);
          }}
        >
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
            >
              {/*申請番号 */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label={`申請番号: ${record.applicationNumber}`}
                  color="primary"
                  sx={{}}
                />
                <Chip
                  label={`申請日：${dayjs(record.submittedAt).isValid() ? dayjs(record.submittedAt).format("YYYY/MM/DD") : "未提出"}`}
                  variant="outlined"
                  color="success"
                />
              </Box>
              {/* 右上の「急ぎ申請」マーク */}
              <Box sx={{ mr: 1 }}>
                {record.isUrgent === true && (
                  <Box sx={{ mt: 1, color: "orange" }}>
                    <NotificationImportantRoundedIcon />
                  </Box>
                )}
              </Box>
            </Box>
            {/* 申請種類：申請書 */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                mb: 0.5,
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography variant="h3" align="center">
                  {record.applicationType}
                </Typography>
                <Typography variant="h3" align="center">
                  :
                </Typography>
                <Typography variant="h3" align="center" color="textSecondary">
                  {record.applicationFormat}
                </Typography>
              </Box>
              <Typography variant="body1" align="center">
                {record.description}
              </Typography>
            </Box>
            {/*テーブル */}
            <TableContainer>
              <Table
                size="small"
                aria-label="application details table"
                sx={{ tableLayout: "auto" }} // フォームデータによってレイアウトを自動的に調整する
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1">申請者</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1">所属</Typography>
                    </TableCell>
                    {/* userProfiles の key を動的に取得 */}
                    {record.userProfiles.map((profile) => (
                      <TableCell key={profile.key} sx={{ textAlign: "center" }}>
                        <Typography variant="body1">{profile.key}</Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1">ステータス</Typography>
                    </TableCell>
                    {record.applicationStatus === "InProgress" && (
                      <TableCell sx={{ textAlign: "center" }}>
                        <Typography variant="body1">進行中アクティビティ</Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body2">
                        {record.applicant}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body2">
                        {Array.isArray(record.userGroup)
                          ? record.userGroup.map((g, i) => (
                            <span key={i}>
                              {g}
                              {i < record.userGroup.length - 1 && <br />}
                            </span>
                          ))
                          : record.userGroup}
                      </Typography>
                    </TableCell>
                    {/* userProfiles の value を動的に取得 */}
                    {record.userProfiles.map((profile) => (
                      <TableCell key={profile.key} sx={{ textAlign: "center" }}>
                        <Typography variant="body2">{profile.value}</Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body2">
                        {statusMapping[record.applicationStatus] ||
                          record.applicationStatus}
                      </Typography>
                    </TableCell>
                    {record.applicationStatus === "InProgress" && (
                      <TableCell sx={{ textAlign: "center" }}>
                        <Typography variant="body2">
                          {record.activities?.slice()
                            .filter((item) => { return item.activityStatus === 'InProgress'; })
                            ?.filter((item, index) => { return index === 0; })
                            .map((item) =>
                              item.activityName
                            )}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* 申請進捗 */}
            <InquiryActivityStepper record={record} />
          </CardContent>
        </Card>
      ))}

      {allLoaded && (
        <Typography variant="body1" sx={{ marginTop: 2, textAlign: "center" }}>
          これ以上ありません！
        </Typography>
      )}
    </Box>
  );
};

export default HistoryRecords;
